import { Category, CategoryService, PageService, ProductService } from '@core/services/steelfab';
import { PageContent } from '@core/services/steelfab/page.service';
import { Product } from '@core/services/steelfab/product.service';
import { Component, DefineDependencies } from '@rhtml/component';
import { Reader } from '@rhtml/di';
import { AngularLayout } from '@rhtml/modifiers';
import { Container } from '@rxdi/core';
import { deepCopy } from '@rxdi/graphql-client';
import { html, LitElement, state, unsafeCSS, unsafeHTML } from '@rxdi/lit-html';
import { Router } from '@rxdi/router';
import { ScrollToService } from '@shared/services';
import { SteelFabTheme } from '@shared/styles/theme';
import * as HeroComponentCSS from 'bundle-text:./hero.component.css';
import { combineLatest, lastValueFrom, timer } from 'rxjs';
import { delay, map, startWith, switchMap, take, tap } from 'rxjs/operators';

import { ProductsListComponent } from '../../products/products-list/products-list.component';
import { Loading } from './hero.component-loading';

/**
 * @customElement hero-component
 */
@Component({
  Settings: {
    selector: 'hero-component',
    styles: [SteelFabTheme, unsafeCSS(HeroComponentCSS)],
    modifiers: [...AngularLayout],
  },
  Providers: DefineDependencies(ProductService, CategoryService, PageService, ScrollToService)(Container),
  State(this: HeroComponent, [productService, categoryService, pageService]) {
    return combineLatest([
      productService.getProducts().pipe(startWith([] as Product[])),
      pageService.getPageById('home-hero'),
      categoryService.getCategoryTree().pipe(startWith([] as Category[])),
    ]).pipe(
      switchMap(([products, page, categories]) =>
        timer(0, 5000).pipe(
          map(() => ({
            products,
            categories,
            product: products[Math.floor(products.length * Math.random())],
            page,
          }))
        )
      )
    );
  },
  Loading,
  Render: ([, , , scrollToService]) =>
    function (this: HeroComponent, state) {
      const { page, product, categories } = deepCopy(state);
      return html`
        <section class="hero">
          <div class="hero-glow"></div>
          <div class="container">
            <div class="hero-inner">
              <div class="hero-content">
                <h1 class="hero-title">${unsafeHTML(page?.title)}</h1>

                <p class="hero-subtitle">${page?.subtitle}</p>
                <p class="hero-subtitle">${page?.body}</p>
                <div class="hero-actions">
                  <button class="btn btn-primary btn-lg" @click=${() => this.router.go('/products')}>
                    Разгледайте Продуктите
                  </button>
                  <button
                    class="btn btn-secondary btn-lg"
                    @click=${() => this.scrollToSection('contact-component')([scrollToService])}
                  >
                    Запитване
                  </button>
                </div>

                <div class="hero-stats">
                  <div class="stat">
                    <span class="stat-number">25<span>+</span></span>
                    <span class="stat-label">Години опит</span>
                  </div>
                  <div class="stat">
                    <span class="stat-number">500<span>+</span></span>
                    <span class="stat-label">Доволни клиенти</span>
                  </div>
                  <div class="stat">
                    <span class="stat-number">1 000<span>+</span></span>
                    <span class="stat-label">Машини доставени</span>
                  </div>
                </div>
              </div>

              <div class="hero-visual">
                <div class="machine-card" @click=${() => this.goto(product.categorySlug, product.slug)(categories)}>
                  <div class="machine-card-header">
                    <div class="machine-dot"></div>
                    <span class="machine-card-title">${product?.name}</span>
                  </div>
                  <div class="machine-placeholder">
                    <img ngIf=${product?.images[0]?.url} src=${product?.images[0]?.url} />
                    <sl-skeleton
                      ngIf=${!product?.images[0]?.url}
                      effect="sheen"
                      style="width: 100%; height: 350px; display: block; --border-radius: var(--sf-radius-md);"
                    ></sl-skeleton>
                  </div>
                  <p style="color:white">${product?.shortDescription}</p>
                  <div class="machine-specs">
                    ${product?.specifications?.map(
                      (v) => html` <div class="spec-pill"><strong>${v.key}</strong>${v.value}</div> `
                    )}
                  </div>
                </div>
                <!-- 
              <div class="floating-badge">
                <div class="floating-badge-icon">✅</div>
                <div class="floating-badge-text">
                  <strong>Гаранция included</strong>
                  <span>Сервиз и резервни части</span>
                </div>
              </div> -->
              </div>
            </div>
          </div>
        </section>
      `;
    },
})
export class HeroComponent extends LitElement {
  @state()
  page: PageContent;

  @state() categoryTree: Category[] = [];

  @Router()
  private router: Router;

  private scrollToSection(componentName: string): Reader<[ScrollToService], void> {
    return async ([scrollToService]) => {
      if (this.router.activePath !== '/') {
        this.router.go('/');
        await lastValueFrom(
          this.router.onSnapshotChange().pipe(
            delay(10),
            take(1),
            tap(() => scrollToService.scrollToElement(componentName))
          )
        );
        return;
      }
      scrollToService.scrollToElement(componentName);
    };
  }

  private goto(categorySlug: string, productSlug?: string | null) {
    return (categories: Category[]) => {
      if (productSlug) {
        // Find the product's category in the tree to build the full ancestor path
        const catNode = ProductsListComponent.findNodeBySlug(categorySlug, categories);

        let segments: string[];

        if (catNode) {
          const ancestorPath = ProductsListComponent.buildAncestorPath(catNode, categories);
          segments = ancestorPath
            .replace(/^\/products\/?/, '')
            .split('/')
            .filter(Boolean);
        } else {
          segments = [categorySlug];
        }

        // Ensure exactly 3 category segments (group/brand/subtype) by repeating the last one
        while (segments.length < 3) {
          segments.push(segments[segments.length - 1]);
        }

        this.router.go(`/products/${segments.join('/')}/${productSlug}`);
      } else if (categorySlug) {
        this.router.go(`/products/${categorySlug}`);
      } else {
        this.router.go(`/products`);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
  }
}
